import React, { Dispatch, FC, ReactNode, SetStateAction } from 'react';

import { styled } from '@mui/material';
import { alpha, css } from '@mui/material/styles';
import { scroller } from 'react-scroll';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import Duration from '~/components/Duration';
import Pip from '~/components/Pip/Pip';
import Ratio from '~/components/Ratio';
import VideoPlayer from '~/components/VideoPlayer/VideoPlayer';
import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { useThemeContext } from '~/context/ThemeProvider/ThemeProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import ThumbBlock from '~/pages/SingleVideoPage/components/ThumbBlock';
import { IUseVideoReview } from '~/pages/SingleVideoPage/hooks/useVideoReview';
import {
  BIG_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  MIDDLE_RADIUS_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
  useIsWidthUp,
} from '~/theme';
import { Maybe, QuizQuestion, Video } from '~/types';

const PORTRAIT_RATIO = 126;

interface VideoPlayerLayoutProps {
  videoReview?: IUseVideoReview;
  onThumbClick?: () => void;
  bumperUrl: Maybe<string> | undefined;
  question?: Maybe<QuizQuestion> | undefined;
  renderChildren?:
    | (({ setShowBumper }: { setShowBumper: (isOpen: boolean) => void }) => ReactNode)
    | undefined;
  playerWrapperRef: React.RefObject<HTMLElement>;
  video: Video | null;
  rating?: Maybe<number> | undefined;
  videoFinished: boolean;
  pipControls: any;
  isXApiPage?: boolean;
  setIsHeaderHidden?: Dispatch<SetStateAction<boolean>>;
  verseVideoId: Maybe<string> | undefined;
  playerRef: React.RefObject<HTMLFrameElement>;
  playerInfo?: JSX.Element;
  loadingVerseVideoId?: boolean;
  expandAnchorId?: string;
}

const VideoPlayerLayout: FC<VideoPlayerLayoutProps> = ({
  videoReview,
  rating,
  onThumbClick,
  bumperUrl,
  question,
  renderChildren,
  playerWrapperRef,
  video,
  videoFinished,
  pipControls,
  verseVideoId,
  playerRef,
  playerInfo,
  isXApiPage,
  setIsHeaderHidden,
  loadingVerseVideoId,
  expandAnchorId,
}) => {
  const isMobile = !useIsWidthUp('md');
  const isSsku = useIsSsku();
  const { isDeviceInPortraitMode } = useThemeContext();
  const withoutNavigation = useWithoutNavigation();
  const { hasAccessToContent } = useUserInfoContext();
  const hasAccess = hasAccessToContent || isXApiPage;
  const VideoWrapper = videoFinished && isMobile ? NoRatio : StyledRatio;

  const duration = video?.duration || 0;

  return (
    <>
      {loadingVerseVideoId || !verseVideoId ? (
        <>
          <LoadingVideoWrapper>
            <VideoWrapper
              ratio={isMobile && isSsku && isDeviceInPortraitMode ? PORTRAIT_RATIO : undefined}
            >
              <AnimatedSkeleton xs="100%" />
            </VideoWrapper>
          </LoadingVideoWrapper>
          {playerInfo && (
            <LoadingVideoInfo
              xs={SKELETON_TEXT_HEIGHT_PX}
              lines={2}
              spacer={TINY_MARGIN_PX}
              lastLineWidth={70}
            />
          )}
        </>
      ) : (
        <>
          <VideoWrapper
            // @ts-ignore
            ref={playerWrapperRef}
            hideVideoIframe={isMobile && videoFinished}
            hasBg={pipControls.active}
            isMobile={isMobile}
            ratio={isMobile && isSsku && isDeviceInPortraitMode ? PORTRAIT_RATIO : undefined}
          >
            <Pip
              videoFinished={videoFinished}
              controls={pipControls}
              fullSizeWrapperRef={playerWrapperRef}
              onExpandClick={() => {
                if (expandAnchorId) {
                  scroller.scrollTo(expandAnchorId, {
                    duration: 1000,
                    smooth: 'easeInOutQuart',
                    offset: -80,
                  });
                }
                pipControls.disable();
              }}
              pipFooterContent={
                <>
                  <StyledDuration duration={duration} />
                  {!withoutNavigation && !isXApiPage && hasAccess && (
                    <ThumbBlock
                      rating={rating}
                      thumbSelected={videoReview?.thumbSelected}
                      likedVideo={videoReview?.likedVideo}
                      leaveReview={videoReview?.leaveReview}
                      setLikedOutsideCommentSection={videoReview?.setLikedOutsideCommentSection}
                      handleOpenComments={onThumbClick}
                    />
                  )}
                </>
              }
              displayCenteredButton={videoFinished}
            >
              <StyledVideoPlayer
                id="player"
                mobOrientation={video?.showing_mode}
                duration={video?.duration}
                title={video?.title}
                expertName={video?.user?.name}
                ref={playerRef}
                verseId={verseVideoId}
                bumperVideoUrl={video?.show_bumper ? bumperUrl : null}
                onToggleFullscreen={setIsHeaderHidden} //SET IS HEADER HIDDEN
                //used to allow drag `n drop on ordering questions over iframe
                disablePointerEvents={Boolean(videoFinished && !isXApiPage && question)}
              >
                {renderChildren}
              </StyledVideoPlayer>
            </Pip>
          </VideoWrapper>
          {playerInfo && <VideoInfoWrapper>{playerInfo}</VideoInfoWrapper>}
        </>
      )}
    </>
  );
};

export default VideoPlayerLayout;

const wrapperWidthCss = ({ theme }) => css`
  width: calc(100% + 16px + 16px);
  margin-left: -16px;
  margin-right: -16px;

  ${theme.breakpoints.up('md')} {
    width: unset;
    margin-left: unset;
    margin-right: unset;
  }
`;
const VideoInfoWrapper = styled('div')`
  ${wrapperWidthCss};
  margin-bottom: ${MIDDLE_MARGIN_PX};
  padding: ${SMALL_MARGIN_PX} ${SMALL_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX} ${SMALL_MARGIN_PX};
  border-radius: 0 0 ${MIDDLE_RADIUS_PX} ${MIDDLE_RADIUS_PX};
  background-color: ${({ theme }) => theme.palette.common.blockBackground.light};
`;
const NoRatio = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hideVideoIframe' && prop !== 'hasBg',
})<{
  hideVideoIframe?: boolean;
}>`
  ${wrapperWidthCss};
  overflow: hidden;
  border-radius: 10px 10px 0 0;

  & iframe {
    display: ${({ hideVideoIframe }) => (hideVideoIframe ? 'none' : 'block')};
  }
`;
export const VideoOverlay = styled('div', {
  shouldForwardProp: (prop) => prop !== 'transparent',
})<{
  transparent: boolean;
}>`
  position: relative;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.palette.common.card.overlay.video};

  ${({ theme }) => theme.breakpoints.up('md')} {
    position: absolute;
    background: ${({ transparent, theme }) =>
      transparent ? 'transparent' : alpha(theme.palette.common.card.overlay.video, 0.9)};
  }
`;
const StyledVideoPlayer = styled(VideoPlayer, {
  shouldForwardProp: (prop) => prop !== 'disablePointerEvents',
})<{ disablePointerEvents: boolean }>`
  width: 100%;
  height: 100%;
  ${({ disablePointerEvents }) => disablePointerEvents && 'pointer-events: none;'}
`;
export const StyledDuration = styled(Duration)`
  margin-right: auto;

  & p {
    color: ${({ theme }) => theme.palette.common.white};
    margin-bottom: 0;
  }

  & svg {
    width: 18px;
    height: 18px;
  }
`;
const StyledRatio = styled(Ratio, {
  shouldForwardProp: (prop) => prop !== 'activePip' && prop !== 'isMobile' && prop !== 'hasBg',
})<{ hasBg?: boolean; isMobile?: boolean }>`
  ${wrapperWidthCss};
  overflow: ${({ isMobile }) => isMobile && 'hidden'};
  border-radius: 10px 10px 0 0;
  background: ${({ theme, hasBg }) => hasBg && theme.palette.common.blackPure};
  transition: 0.9s background;
`;
const LoadingVideoWrapper = styled('div')`
  margin-bottom: ${SMALL_MARGIN_PX};
`;
const LoadingVideoInfo = styled(AnimatedSkeletonMultiline)`
  max-width: 70%;
  margin-bottom: ${BIG_MARGIN_PX};
`;
